import type { BusinessProfileBusiness } from "../services/businessProfileService";
import { logger } from "./logger";

export class UnauthorizedBusinessAccessError extends Error {
  readonly businessId: string;

  constructor(businessId: string) {
    super("Unauthorized business context access attempt");
    this.name = "UnauthorizedBusinessAccessError";
    this.businessId = businessId;
  }
}

const normalizeBusinessId = (businessId: string | null | undefined): string =>
  (businessId ?? "").trim();

export const getAllowedBusinessIds = (
  businesses: readonly BusinessProfileBusiness[] | null | undefined
): Set<string> => {
  const allowed = new Set<string>();

  (businesses ?? []).forEach((business) => {
    const id = normalizeBusinessId(business?.businessId);
    if (id) {
      allowed.add(id);
    }
  });

  return allowed;
};

export const isBusinessAccessAllowed = (
  businessId: string | null | undefined,
  businesses: readonly BusinessProfileBusiness[] | null | undefined
): boolean => {
  const normalized = normalizeBusinessId(businessId);

  if (!normalized) {
    return true;
  }

  return getAllowedBusinessIds(businesses).has(normalized);
};

export const assertBusinessAccess = (
  businessId: string | null | undefined,
  businesses: readonly BusinessProfileBusiness[] | null | undefined
): void => {
  if (isBusinessAccessAllowed(businessId, businesses)) {
    return;
  }

  const normalized = normalizeBusinessId(businessId);

  logger.warn(
    "[businessAccess] Blocked attempt to use a business context outside the user's authorized businesses",
    { businessId: normalized }
  );

  throw new UnauthorizedBusinessAccessError(normalized);
};
